import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  ReferenceLine,
} from 'recharts';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { REPS_COLORS } from '../../utils/constants';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import { useSiteComparisons } from '../../hooks/useSiteComparisons';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../firebase';
import OrgSwitcher from '../../components/OrgSwitcher';
import { useDemoRole } from '../../../../contexts/DemoRoleContext';

const DIVERGENCE_COLORS = {
  survey_low: '#c62828',
  survey_high: '#ef6c00',
  aligned: '#2e7d32',
};

const QUADS = ['reliability', 'efficiency', 'productivity', 'safety'];
const overallOf = (s) => QUADS.reduce((a, q) => a + (s[q] || 0), 0) / 4;
const toBars = (name, scores) => ({
  name,
  Reliability: scores.reliability,
  Efficiency: scores.efficiency,
  Productivity: scores.productivity,
  Safety: scores.safety,
});

/**
 * Comparisons — Mindmap branch 3, "Algorithms → Comparisons"
 * LIVE: computed from the active organization's snapshots and responses.
 *   - Team to Team within a Department
 *   - Dept to Dept within a Site
 *   - Sites within a Region
 *   - All sites within the Enterprise
 * Divergence alerts (survey vs actual) stream live from Workflow 6 (Hugo_HealthMessages).
 */
function Comparisons() {
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const organization = organizations[0] || null;
  const { comparisons, loading } = useSiteComparisons(organization?.id || null);

  const [mode, setMode] = useState('team');
  const { queryScope } = useDemoRole();
  // Scoped members compare only within their own branch: site-scoped users
  // lose the region/enterprise levels; dept/team-scoped users are narrowed
  // further by the option filters below.
  const lockSiteId = queryScope?.siteId || null;
  const lockDeptId = queryScope?.deptId || null;
  useEffect(() => {
    if (lockSiteId && (mode === 'site' || mode === 'enterprise')) setMode('team');
  }, [lockSiteId, mode]);
  const [deptId, setDeptId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [regionId, setRegionId] = useState('');

  // Live divergence alerts — Hugo_HealthMessages carrying survey-vs-actual deltas
  const [healthMessages, setHealthMessages] = useState([]);
  useEffect(() => {
    if (!organization?.id) {
      setHealthMessages([]);
      return undefined;
    }
    const q = query(collection(db, 'Hugo_HealthMessages'), where('organizationId', '==', organization.id));
    return onSnapshot(
      q,
      (snap) => {
        const msgs = snap.docs.map((d) => {
          const m = { id: d.id, ...d.data() };
          const t = m.triggeredAt;
          return { ...m, triggeredAt: t?.toDate ? t.toDate() : t };
        });
        msgs.sort((a, b) => new Date(b.triggeredAt) - new Date(a.triggeredAt));
        setHealthMessages(msgs);
      },
      () => setHealthMessages([])
    );
  }, [organization?.id]);

  const divergences = healthMessages.filter((m) => m.quadrant && m.deltas && m.deltas[m.quadrant]);
  const divergenceSiteName = (msg) =>
    organization?.sites?.find((s) => s.id === msg.scope?.siteId)?.name ||
    comparisons?.siteScores?.find((s) => s.siteId === msg.scope?.siteId)?.siteName ||
    organization?.name ||
    'Organization';

  // Departments that actually have team-level data, sites with dept data, etc.
  const deptOptions = useMemo(() => {
    if (!comparisons) return [];
    const withTeams = new Set(comparisons.teamScores.map((t) => t.departmentId));
    return comparisons.deptScores
      .filter((d) => withTeams.has(d.deptId))
      .filter((d) => (lockDeptId ? d.deptId === lockDeptId : !lockSiteId || d.siteId === lockSiteId))
      .map((d) => {
        const site = comparisons.siteScores.find((s) => s.siteId === d.siteId);
        return { id: d.deptId, label: `${d.deptName} — ${site ? site.siteName : ''}` };
      });
  }, [comparisons, lockSiteId, lockDeptId]);

  const siteOptions = useMemo(() => {
    if (!comparisons) return [];
    const withDepts = new Set(comparisons.deptScores.map((d) => d.siteId));
    return comparisons.siteScores
      .filter((s) => withDepts.has(s.siteId))
      .filter((s) => !lockSiteId || s.siteId === lockSiteId);
  }, [comparisons, lockSiteId]);

  // P3-08 (Tim C0813: "ops manager vs other plants' ops departments"): the same department name
  // across every site. Names are matched case-insensitively; visible to unscoped viewers and to
  // site-scoped ones (comparing your department to its peers elsewhere is the point).
  const [deptName, setDeptName] = useState('');
  const deptNameOptions = useMemo(() => {
    if (!comparisons) return [];
    const counts = {};
    comparisons.deptScores.forEach((d) => {
      const key = String(d.deptName || '').trim().toLowerCase();
      if (!key) return;
      counts[key] = counts[key] || { key, label: d.deptName, sites: 0 };
      counts[key].sites += 1;
    });
    return Object.values(counts).filter((c) => c.sites > 1).sort((a, b) => a.label.localeCompare(b.label));
  }, [comparisons]);
  const activeDeptName = deptName || (lockDeptId && deptNameOptions.find((o) => o.key === String(comparisons?.deptScores.find((d) => d.deptId === lockDeptId)?.deptName || '').trim().toLowerCase())?.key) || deptNameOptions[0]?.key || '';

  const activeDept = deptId || deptOptions[0]?.id || '';
  const activeSite = siteId || siteOptions[0]?.siteId || '';
  const activeRegion = regionId || comparisons?.regions[0]?.id || '';

  let title = '';
  let description = '';
  let chartData = [];
  let benchmarkLabel = '';
  let benchmarkValue = null;
  let selector = null;

  if (comparisons) {
    if (mode === 'team') {
      const teams = comparisons.teamScores.filter((t) => t.departmentId === activeDept);
      const deptLabel = deptOptions.find((d) => d.id === activeDept)?.label || '';
      title = 'Team-to-Team (within Department)';
      description = `Compare every team inside ${deptLabel}. Outliers indicate where attention is needed.`;
      chartData = teams.map((t) => toBars(t.teamName, t.scores));
      benchmarkLabel = 'Dept avg';
      benchmarkValue = teams.length
        ? teams.reduce((a, t) => a + overallOf(t.scores), 0) / teams.length
        : null;
      selector = (
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Department</InputLabel>
          <Select label="Department" value={activeDept} onChange={(e) => setDeptId(e.target.value)}>
            {deptOptions.map((d) => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
          </Select>
        </FormControl>
      );
    } else if (mode === 'dept') {
      const depts = comparisons.deptScores.filter((d) => d.siteId === activeSite);
      const siteLabel = comparisons.siteScores.find((s) => s.siteId === activeSite)?.siteName || '';
      title = 'Department-to-Department (within Site)';
      description = `Compare departments at ${siteLabel}. Cross-departmental gaps reveal organization-level patterns.`;
      chartData = depts.map((d) => toBars(d.deptName, d.scores));
      benchmarkLabel = 'Site avg';
      benchmarkValue = depts.length
        ? depts.reduce((a, d) => a + overallOf(d.scores), 0) / depts.length
        : null;
      selector = (
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Site</InputLabel>
          <Select label="Site" value={activeSite} onChange={(e) => setSiteId(e.target.value)}>
            {siteOptions.map((s) => <MenuItem key={s.siteId} value={s.siteId}>{s.siteName}</MenuItem>)}
          </Select>
        </FormControl>
      );
    } else if (mode === 'peers') {
      const peers = comparisons.deptScores.filter((d) => String(d.deptName || '').trim().toLowerCase() === activeDeptName);
      const label = deptNameOptions.find((o) => o.key === activeDeptName)?.label || 'Department';
      title = 'Same Department across Sites';
      description = `Compare every ${label} department across ${comparisons.org.name}. Your own department is highlighted by name; the benchmark is the average of its peers.`;
      chartData = peers.map((d) => {
        const site = comparisons.siteScores.find((sc) => sc.siteId === d.siteId);
        return toBars(`${site ? site.siteName : d.siteId}${d.deptId === lockDeptId ? ' (mine)' : ''}`, d.scores);
      });
      benchmarkLabel = 'Peer avg';
      benchmarkValue = peers.length ? peers.reduce((a, d) => a + overallOf(d.scores), 0) / peers.length : null;
      selector = (
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Department</InputLabel>
          <Select label="Department" value={activeDeptName} onChange={(e) => setDeptName(e.target.value)}>
            {deptNameOptions.map((o) => <MenuItem key={o.key} value={o.key}>{o.label} ({o.sites} sites)</MenuItem>)}
          </Select>
        </FormControl>
      );
    } else if (mode === 'site') {
      const region = comparisons.regions.find((r) => r.id === activeRegion);
      title = 'Sites within Region';
      description = `Compare every site inside ${region ? region.name : 'the region'} against the regional average.`;
      chartData = (region?.sites || []).map((s) => toBars(s.siteName, s.scores));
      const ravg = comparisons.regionalAverages[activeRegion];
      benchmarkLabel = 'Region avg';
      benchmarkValue = ravg ? overallOf(ravg) : null;
      selector = (
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Region</InputLabel>
          <Select label="Region" value={activeRegion} onChange={(e) => setRegionId(e.target.value)}>
            {comparisons.regions.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
          </Select>
        </FormControl>
      );
    } else if (mode === 'enterprise') {
      title = 'All Sites within Enterprise';
      description = `Compare every site across ${comparisons.org.name}.`;
      chartData = comparisons.siteScores.map((s) => toBars(s.siteName, s.scores));
      benchmarkLabel = 'Enterprise avg';
      benchmarkValue = overallOf(comparisons.enterpriseAverage);
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <CompareArrowsIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Comparisons Engine</Typography>
        <OrgSwitcher />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Compare REPS scores across any level of {organization ? organization.name : 'the organization'} —
        computed live from survey data. Higher scores mean more Organizational Entropy (worse).
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : !comparisons || comparisons.siteScores.length === 0 ? (
        <Alert severity="info">
          No comparison data yet — comparisons appear once your organization has survey responses
          rolled up at the site level.
        </Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_, v) => v && setMode(v)}
            >
              <ToggleButton value="team" disabled={deptOptions.length === 0}>Team to Team</ToggleButton>
              <ToggleButton value="dept" disabled={siteOptions.length === 0}>Dept to Dept</ToggleButton>
              <ToggleButton value="peers" disabled={deptNameOptions.length === 0}>Same Dept across Sites</ToggleButton>
              {!lockSiteId && <ToggleButton value="site">Sites in Region</ToggleButton>}
              {!lockSiteId && <ToggleButton value="enterprise">Enterprise-wide</ToggleButton>}
            </ToggleButtonGroup>
            {selector}
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{title}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {description}
              </Typography>
              {chartData.length === 0 ? (
                <Alert severity="info">
                  Not enough recent responses at this level — team and department comparisons use
                  the last 14 days of survey data.
                </Alert>
              ) : (
                <Box sx={{ height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" interval={0} angle={chartData.length > 6 ? -25 : 0} textAnchor={chartData.length > 6 ? 'end' : 'middle'} height={chartData.length > 6 ? 70 : 30} />
                      <YAxis domain={[0, 10]} />
                      <Tooltip />
                      <Legend />
                      {benchmarkValue && (
                        <ReferenceLine
                          y={benchmarkValue}
                          stroke="#666"
                          strokeDasharray="4 4"
                          label={{ value: benchmarkLabel, position: 'right', fill: '#666' }}
                        />
                      )}
                      <Bar dataKey="Reliability" fill={REPS_COLORS.reliability} />
                      <Bar dataKey="Efficiency" fill={REPS_COLORS.efficiency} />
                      <Bar dataKey="Productivity" fill={REPS_COLORS.productivity} />
                      <Bar dataKey="Safety" fill={REPS_COLORS.safety} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Survey vs Actual divergences — live from Workflow 6 (Divergence Detection) */}
          <Typography variant="h6" gutterBottom>Survey vs Actual — Divergence Alerts</Typography>
          <Grid container spacing={2}>
            {divergences.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  No divergence alerts — survey scores and field data currently agree.
                  The Divergence Detection assistant checks every 15 minutes.
                </Alert>
              </Grid>
            )}
            {divergences.map((m) => {
              const d = m.deltas[m.quadrant];
              const borderColor = DIVERGENCE_COLORS[d.direction] || '#666';
              return (
                <Grid item xs={12} md={6} key={m.id}>
                  <Card variant="outlined" sx={{ borderLeft: 6, borderLeftColor: borderColor, height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" fontWeight={700}>{divergenceSiteName(m)}</Typography>
                        <Chip label={m.quadrant} size="small" variant="outlined" />
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          <Chip label={`Survey ${d.survey}`} size="small" variant="outlined" />
                          <Chip label={`Actual ${d.actual}`} size="small" variant="outlined" />
                          <Chip label={`Δ ${d.delta}`} size="small" sx={{ bgcolor: borderColor, color: '#fff', fontWeight: 700 }} />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {m.summary}
                      </Typography>
                      {m.triggeredAt && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {new Date(m.triggeredAt).toLocaleString()}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
}

export default Comparisons;
